import { useNotification } from "@refinedev/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { api, ApiError } from "../api";
import { DATE_TIME_FORMAT, formatKurus, planLabel } from "../labels";
import type { OfficePricing, PricedPlan, PricingParams, PricingResult } from "../types";
import { durationLabel } from "./plans";

// Percentages and lira on the form; basis points and kuruş for the API.
type Values = {
  hourPriceLira: number;
  volumeDiscountPct: number;
  durationDiscountPct: Record<string, number>;
  paymentFeePct: number;
  usagePct: Record<string, number>;
  renewalPct: Record<string, number>;
  minMarginPct: number;
  decoyGapPct: number;
  roundingLira: number;
  /** The tutor rate per lesson hour; not part of PricingParams. */
  tutorRateLira: number;
};

const toPct = (bps: number) => bps / 100;
const toBps = (pct: number) => Math.round((pct ?? 0) * 100);
const mapValues = (record: Record<string, number>, map: (value: number) => number) =>
  Object.fromEntries(Object.entries(record).map(([key, value]) => [key, map(value)]));

function toValues(params: PricingParams, tutorRateKurus: number): Values {
  return {
    tutorRateLira: tutorRateKurus / 100,
    hourPriceLira: params.hourPriceKurus / 100,
    volumeDiscountPct: toPct(params.volumeDiscountBps),
    durationDiscountPct: mapValues(params.durationDiscountBps, toPct),
    paymentFeePct: toPct(params.paymentFeeBps),
    usagePct: mapValues(params.usageBps, toPct),
    renewalPct: mapValues(params.renewalBps, toPct),
    minMarginPct: toPct(params.minMarginBps),
    decoyGapPct: toPct(params.decoyGapBps),
    roundingLira: params.roundingKurus / 100,
  };
}

/** The request body of the preview and of "Uygula". */
function toBody(values: Values) {
  return {
    params: toParams(values),
    tutorRateKurus: Math.round((values.tutorRateLira ?? 0) * 100),
  };
}

function toParams(values: Values): PricingParams {
  return {
    hourPriceKurus: Math.round((values.hourPriceLira ?? 0) * 100),
    volumeDiscountBps: toBps(values.volumeDiscountPct),
    durationDiscountBps: mapValues(values.durationDiscountPct ?? {}, toBps),
    paymentFeeBps: toBps(values.paymentFeePct),
    usageBps: mapValues(values.usagePct ?? {}, toBps),
    renewalBps: mapValues(values.renewalPct ?? {}, toBps),
    minMarginBps: toBps(values.minMarginPct),
    decoyGapBps: toBps(values.decoyGapPct),
    roundingKurus: Math.round((values.roundingLira ?? 0) * 100),
  };
}

const lira = formatKurus;
const percent = (bps: number) => `%${(bps / 100).toLocaleString("tr-TR")}`;

function change(plan: PricedPlan) {
  if (plan.currentPriceKurus === plan.priceKurus) return null;
  const pct = ((plan.priceKurus - plan.currentPriceKurus) / plan.currentPriceKurus) * 100;
  return (
    <Typography.Text type={pct > 0 ? "success" : "danger"}>
      {pct > 0 ? "+" : ""}
      {pct.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}%
    </Typography.Text>
  );
}

const pctInput = (max = 100) => (
  <InputNumber min={0} max={max} step={1} precision={1} addonBefore="%" style={{ width: "100%" }} />
);

/**
 * /plans/pricing (permission `plans.manage`; product decision 2026-10-10): the pricing model.
 * Every plan's price, "Popüler" and the duration the plans page opens on follow from these
 * values; the preview shows the result before it is applied to all plans at once. Current
 * subscribers keep their price; the change reaches new purchases (docs/MVP.md → Pricing model).
 */
export function PricingPage() {
  const navigate = useNavigate();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Values>();
  // The preview of the values being edited; until the first edit, the one loaded with them.
  const [edited, setPreview] = useState<PricingResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const request = useRef(0);

  const { data, error } = useQuery({
    queryKey: ["office", "pricing"],
    queryFn: () => api<OfficePricing>("/api/office/pricing"),
  });

  useEffect(() => {
    if (!data) return;
    form.setFieldsValue(toValues(data.params, data.tutorRateKurus));
  }, [data, form]);

  useEffect(() => () => clearTimeout(timer.current), []);

  function refreshPreview() {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const id = ++request.current;
      try {
        const result = await api<PricingResult>("/api/office/pricing/preview", {
          method: "POST",
          body: toBody(form.getFieldsValue(true)),
        });
        if (id !== request.current) return;
        setPreview(result);
        setPreviewError(null);
      } catch (cause) {
        if (id !== request.current) return;
        setPreviewError(cause instanceof ApiError ? cause.message : "Önizleme hesaplanamadı.");
      }
    }, 400);
  }

  async function apply() {
    setApplying(true);
    try {
      await api("/api/office/pricing/apply", {
        method: "POST",
        body: toBody(await form.validateFields()),
      });
      await queryClient.invalidateQueries({ queryKey: ["office"] });
      // The loaded preview now shows the applied prices as today's.
      setPreview(null);
      open?.({ type: "success", message: "Fiyatlar bütün planlara uygulandı." });
    } catch (cause) {
      if (cause instanceof ApiError) open?.({ type: "error", message: cause.message });
    } finally {
      setApplying(false);
    }
  }

  if (error) {
    return (
      <Alert type="error" message={error instanceof ApiError ? error.message : "Yüklenemedi."} />
    );
  }
  if (!data) return <Card loading />;

  const preview = edited ?? data.preview;
  const plans = preview?.plans ?? [];
  const hours = [...new Set(plans.map((plan) => plan.weeklyHours))].sort((a, b) => a - b);
  // Months plans by length, then the season plans (product decision 2026-10-11).
  const months = [
    ...new Set(plans.flatMap((plan) => (plan.durationMonths ? [plan.durationMonths] : []))),
  ].sort((a, b) => a - b);
  const hasSeason = plans.some((plan) => plan.season);
  const order = (plan: PricedPlan) => plan.durationMonths ?? Number.MAX_SAFE_INTEGER;
  const rows = [...plans].sort((a, b) => order(a) - order(b) || a.weeklyHours - b.weeklyHours);

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Space style={{ justifyContent: "space-between", width: "100%" }} wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Fiyatlandırma
        </Typography.Title>
        <Space>
          <Button onClick={() => navigate("/plans")}>Planlara dön</Button>
          <Popconfirm
            title="Fiyatlar bütün planlara uygulansın mı?"
            description="Yeni fiyatlar yeni alımlarda geçerli olur. Mevcut aboneler kendi fiyatlarıyla yenilenir; fiyat düşerse yeni fiyattan. Eğitmen ücreti bundan sonra biten derslere uygulanır."
            okText="Uygula"
            cancelText="Vazgeç"
            onConfirm={apply}
            disabled={!preview || Boolean(previewError)}
          >
            <Button type="primary" loading={applying} disabled={!preview || Boolean(previewError)}>
              Uygula
            </Button>
          </Popconfirm>
        </Space>
      </Space>

      {data.isDefault ? (
        <Alert
          type="info"
          showIcon
          message="Henüz bir fiyat modeli uygulanmadı. Değerler başlangıç önerisidir; kullanım ve yenileme oranları tahmindir, gerçek veri biriktikçe güncelle."
        />
      ) : null}

      <Form form={form} layout="vertical" onValuesChange={refreshPreview}>
        <Row gutter={16}>
          <Col xs={24} lg={8}>
            <Card title="Fiyat" size="small">
              <Form.Item
                name="hourPriceLira"
                label="Saat fiyatı (en küçük aylık plan)"
                rules={[{ required: true, message: "Saat fiyatını gir." }]}
              >
                <InputNumber min={1} step={10} addonAfter="₺" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                name="volumeDiscountPct"
                label="Her ek haftalık saatte saat fiyatı ne kadar düşsün"
              >
                {pctInput(50)}
              </Form.Item>
              {months.map((count) => (
                <Form.Item
                  key={count}
                  name={["durationDiscountPct", String(count)]}
                  label={`${durationLabel(count)} indirimi`}
                >
                  {pctInput(90)}
                </Form.Item>
              ))}
              {hasSeason ? (
                <Form.Item
                  name={["durationDiscountPct", "season"]}
                  label="Dönem paketi indirimi"
                  extra="Aylık fiyatın haftalığına göre. Satış fiyatı: haftalık fiyat × sınava kalan hafta."
                >
                  {pctInput(90)}
                </Form.Item>
              ) : null}
              <Form.Item name="roundingLira" label="Yuvarlama">
                <InputNumber
                  min={1}
                  max={1000}
                  step={10}
                  addonAfter="₺"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card title="Maliyet ve kâr" size="small">
              <Form.Item
                name="tutorRateLira"
                label="Eğitmen ücreti (ders saati başı)"
                tooltip="Uygula'dan sonra biten derslere uygulanır; daha önce biten dersler eski ücretle kalır."
                rules={[{ required: true, message: "Eğitmen ücretini gir." }]}
              >
                <InputNumber min={1} step={10} addonAfter="₺" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="paymentFeePct" label="Ödeme komisyonu">
                {pctInput(50)}
              </Form.Item>
              <Form.Item
                name="minMarginPct"
                label="Asgari kâr (bütün saatler kullanılsa bile)"
                tooltip="Hiçbir plan bunun altında fiyatlanmaz."
              >
                {pctInput(90)}
              </Form.Item>
              <Form.Item
                name="decoyGapPct"
                label="Popüler'in bir altındaki plan en fazla ne kadar ucuz olsun"
                tooltip="Bir alttaki plan Popüler plana yaklaştırılır; aradaki fark küçük göründüğü için öğrenci üst plana geçer. 0: kapalı."
              >
                {pctInput(90)}
              </Form.Item>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card title="Varsayımlar" size="small">
              {hours.map((weeklyHours) => (
                <Form.Item
                  key={weeklyHours}
                  name={["usagePct", String(weeklyHours)]}
                  label={`${weeklyHours} saatlik planlarda kullanılan saat`}
                  tooltip="Kullanılmayan haftalık saatler yanar; eğitmene ödenmez."
                >
                  {pctInput()}
                </Form.Item>
              ))}
              {months.map((count) => (
                <Form.Item
                  key={count}
                  name={["renewalPct", String(count)]}
                  label={`${durationLabel(count)} planların yenilenme oranı`}
                >
                  {pctInput(95)}
                </Form.Item>
              ))}
            </Card>
          </Col>
        </Row>
      </Form>

      {previewError ? <Alert type="error" showIcon message={previewError} /> : null}
      {preview && preview.warnings.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          message="Kontrol et"
          description={
            <ul style={{ margin: 0, paddingInlineStart: 20 }}>
              {preview.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          }
        />
      ) : null}

      <Card
        title="Önizleme"
        size="small"
        extra={
          preview?.defaultDuration ? (
            <Typography.Text>
              Plan sayfası{" "}
              <strong>
                {preview.defaultDuration === "season"
                  ? "dönem paketi"
                  : durationLabel(preview.defaultDuration)}
              </strong>{" "}
              ile açılır
            </Typography.Text>
          ) : null
        }
      >
        <Table<PricedPlan>
          rowKey="id"
          size="small"
          dataSource={rows}
          pagination={false}
          scroll={{ x: "max-content" }}
          columns={[
            {
              title: "Plan",
              render: (_, plan) => (
                <Space wrap>
                  <span>{planLabel(plan)}</span>
                  {plan.isFeatured ? <Tag color="blue">Popüler</Tag> : null}
                  {plan.isActive ? null : <Tag>Satışta değil</Tag>}
                  {plan.pulledToFeatured ? <Tag color="purple">Popüler'e yaklaştırıldı</Tag> : null}
                  {plan.raisedToFloor ? <Tag color="orange">Tabana yükseltildi</Tag> : null}
                </Space>
              ),
            },
            {
              title: "Şu anki fiyat",
              render: (_, plan) =>
                plan.season
                  ? `${lira(plan.currentPriceKurus)} / hafta`
                  : lira(plan.currentPriceKurus),
            },
            {
              title: "Yeni fiyat",
              render: (_, plan) => (
                <Space>
                  <strong>
                    {plan.season ? `${lira(plan.priceKurus)} / hafta` : lira(plan.priceKurus)}
                  </strong>
                  {change(plan)}
                </Space>
              ),
            },
            {
              title: "Bugün satış",
              render: (_, plan) =>
                plan.season ? (
                  <span title={`${plan.season.weeksLeft} hafta`}>{lira(plan.saleKurus)}</span>
                ) : (
                  lira(plan.saleKurus)
                ),
            },
            { title: "Aylık", render: (_, plan) => lira(plan.monthlyKurus) },
            { title: "Saat başı", render: (_, plan) => lira(plan.perHourKurus) },
            {
              title: "Beklenen eğitmen maliyeti",
              render: (_, plan) => lira(plan.expectedTutorCostKurus),
            },
            { title: "Komisyon", render: (_, plan) => lira(plan.feeKurus) },
            { title: "Dönem kârı", render: (_, plan) => lira(plan.periodProfitKurus) },
            {
              title: "Tam kullanımda marj",
              render: (_, plan) => percent(plan.fullUsageMarginBps),
            },
            {
              title: "Müşteri başı beklenen kâr",
              render: (_, plan) => (
                <span title={`${plan.expectedPeriods.toLocaleString("tr-TR")} dönem`}>
                  {lira(plan.customerProfitKurus)}
                </span>
              ),
            },
          ]}
        />
      </Card>

      <Card title="Geçmiş" size="small">
        <Table
          rowKey="id"
          size="small"
          dataSource={data.history}
          pagination={false}
          locale={{ emptyText: "Henüz uygulanmış bir model yok." }}
          columns={[
            { title: "Tarih", render: (_, row) => dayjs(row.createdAt).format(DATE_TIME_FORMAT) },
            { title: "Eğitmen ücreti", render: (_, row) => lira(row.tutorRateKurus) },
            {
              title: "Plan sayfası",
              render: (_, row) =>
                row.defaultSeason
                  ? "Dönem paketi"
                  : row.defaultDurationMonths
                    ? durationLabel(row.defaultDurationMonths)
                    : "—",
            },
          ]}
        />
        <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
          Kimin uyguladığı ve eski/yeni fiyatlar Denetim kaydında.
        </Typography.Paragraph>
      </Card>
    </Space>
  );
}
